import { useEffect, useRef, useState } from "react";
import { Bot, Check, CheckCheck, Eye, EyeOff, HelpCircle, Loader2, MessageSquareText, Send, Sparkles, Wand2, X, XCircle } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Checkbox, MarkdownBody, NativeSelect, Textarea, cn, saveFailureText, toast, useSave } from "@ncp/ui";
import { repoPaths } from "../api";
import { useBuilder, useStore } from "../store";
import type { CopilotResponse, Graph, GraphChangeSet, GraphIssue, OperationResult } from "../types";

export interface CopilotProposal { response: CopilotResponse; instruction: string; accepted: Set<string>; preview: Graph | null; changes: GraphChangeSet | null; issues: GraphIssue[] }

interface Turn { role: "user" | "assistant"; text: string; response?: CopilotResponse; instruction?: string }

const SUGGESTIONS = ["fastTrack", "addReview", "notify", "deadline", "explain"] as const;

/**
 * The AI Copilot beside the canvas: describe a change, see it as a ghost overlay, accept all or some of the operations, refine in
 * conversation, and commit it as an AI-authored change. Also explains the workflow or a diff.
 */
export function CopilotPanel({ repoKey, proposal, onProposal, onAccepted, diffRange, ghost, onGhost, initial, onInitialConsumed }: {
  repoKey: string; proposal: CopilotProposal | null; onProposal: (p: CopilotProposal | null) => void;
  onAccepted: (instruction: string, summary: string) => void; diffRange?: { from: string; to: string } | null; ghost: boolean; onGhost: (v: boolean) => void;
  /** An instruction to run as soon as the panel opens (a new workflow described in the gallery). */
  initial?: { mode: "generate" | "edit"; text: string }; onInitialConsumed?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const store = useStore();
  const graph = useBuilder((s) => s.graph);
  const meta = useBuilder((s) => s.meta);
  const selection = useBuilder((s) => s.selection);
  const readOnly = useBuilder((s) => s.readOnly);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"edit" | "generate" | "explain" | "explainDiff">("edit");
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [turns]);
  const selectedNodes = graph.nodes.filter((n) => selection.nodes.includes(n.id!));

  // A suggestion stores nothing: its failure is answered in the conversation, with the cause (report: "silent" leaves it to the panel).
  const ask = useSave({
    mutationFn: (vars: { instruction: string; mode: string }) => api.post<CopilotResponse>(`${repoPaths.base(repoKey)}/copilot`, {
      mode: vars.mode, instruction: vars.instruction, graph, meta: { name: meta.name, nameAr: meta.nameAr, description: meta.description, legalBasis: meta.legalBasis },
      selection: selection.nodes, history: turns.slice(-6).map((x) => ({ role: x.role, content: x.role === "assistant" ? x.response?.summary ?? x.text : x.text })),
      diffFrom: vars.mode === "explainDiff" ? diffRange?.from : undefined, diffTo: vars.mode === "explainDiff" ? diffRange?.to : undefined, locale,
    }),
    onSuccess: (r, vars) => {
      setTurns((x) => [...x, { role: "assistant", text: r.markdown ?? r.summary, response: r, instruction: vars.instruction }]);
      if (r.operations.length && r.preview) onProposal({ response: r, instruction: vars.instruction, accepted: new Set(r.results.filter((o) => o.applied).map((o) => o.id)), preview: r.preview, changes: r.changes ?? null, issues: r.issues });
      else onProposal(null);
    },
    report: "silent",
    onError: (e: Error) => { setTurns((x) => [...x, { role: "assistant", text: saveFailureText(e) || t("admin:common.error") }]); },
  });

  // Re-applying the chosen operations to the preview stores nothing either; a failure keeps the previous preview and says why.
  const reapply = useSave({
    mutationFn: (accepted: Set<string>) => api.post<{ graph: Graph; changes: GraphChangeSet; issues: GraphIssue[]; results: OperationResult[] }>("/api/workflows/builder/operations", { graph, operations: proposal!.response.operations, only: [...accepted] }),
    failed: t("admin:common.error"),
    onSuccess: (r, accepted) => onProposal({ ...proposal!, accepted, preview: r.graph, changes: r.changes, issues: r.issues }),
  });

  const send = (text = input, m = mode) => {
    const instruction = text.trim();
    if (!instruction && m !== "explain") return;
    setTurns((x) => [...x, { role: "user", text: instruction || t("admin:builder.copilot.explainPrompt") }]);
    setInput("");
    ask.mutate({ instruction: instruction || "Explain this workflow", mode: m });
  };

  const accept = () => {
    if (!proposal?.preview) return;
    store.replace(structuredClone(proposal.preview), null, "Copilot");
    onAccepted(proposal.instruction, proposal.response.summary);
    onProposal(null);
    // Applied to the canvas only, not stored: the hint says it is saved by committing.
    toast.info(t("admin:builder.copilot.applied"), { description: t("admin:builder.copilot.appliedHint") });
  };

  const toggle = (id: string) => {
    if (!proposal) return;
    const next = new Set(proposal.accepted);
    if (next.has(id)) next.delete(id); else next.add(id);
    reapply.mutate(next);
  };

  const errors = proposal?.issues.filter((i) => i.severity === "Error").length ?? 0;
  const started = useRef(false);
  useEffect(() => {
    if (!initial || started.current) return;
    started.current = true;
    setMode(initial.mode);
    send(initial.text, initial.mode);
    onInitialConsumed?.();
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-sand-200 p-3">
        <span className="grid size-8 place-items-center rounded-lg bg-gradient-to-br from-aegold-500 to-seablue-600 text-white shadow"><Sparkles className="size-4" /></span>
        <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{t("admin:builder.copilot.title")}</p><p className="text-[11px] text-muted-foreground">{t("admin:builder.copilot.subtitle")}</p></div>
        {proposal ? <Button size="icon-sm" variant="ghost" title={ghost ? t("admin:builder.copilot.hideGhost") : t("admin:builder.copilot.showGhost")} onClick={() => onGhost(!ghost)}>{ghost ? <EyeOff /> : <Eye />}</Button> : null}
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {turns.length === 0 ? (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{t("admin:builder.copilot.intro")}</p>
            <div className="space-y-1.5">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" disabled={readOnly && s !== "explain"} onClick={() => (s === "explain" ? send("", "explain") : setInput(t(`admin:builder.copilot.suggestions.${s}`)))}
                  className="flex w-full items-start gap-2 rounded-lg border border-sand-200 bg-white px-2.5 py-2 text-start text-xs transition hover:border-aegold-300 hover:bg-aegold-50/50 disabled:opacity-50">
                  {s === "explain" ? <MessageSquareText className="mt-0.5 size-3.5 shrink-0 text-seablue-600" /> : <Wand2 className="mt-0.5 size-3.5 shrink-0 text-aegold-700" />}{t(`admin:builder.copilot.suggestions.${s}`)}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {turns.map((turn, i) => (
          <div key={i} className={cn("rounded-xl px-3 py-2 text-xs", turn.role === "user" ? "ms-6 bg-aeblack-950 text-white" : "me-2 border border-sand-200 bg-white")}>
            {turn.role === "assistant" ? (
              <>
                <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-sand-500"><Bot className="size-3" /> Copilot {turn.response?.offline ? <Badge size="sm" variant="outline">{t("admin:builder.copilot.offline")}</Badge> : turn.response?.model ? <Badge size="sm" variant="ai">{turn.response.model}</Badge> : null}</div>
                {turn.response?.markdown ? <MarkdownBody className="prose-sm text-xs">{turn.response.markdown}</MarkdownBody> : <p>{locale === "ar" && turn.response?.summaryAr ? turn.response.summaryAr : turn.text}</p>}
                {turn.response?.questions.length ? <ul className="mt-2 space-y-1">{turn.response.questions.map((q, k) => <li key={k} className="flex gap-1.5 text-warning-800"><HelpCircle className="mt-0.5 size-3 shrink-0" />{q}</li>)}</ul> : null}
                {turn.response && turn.response.operations.length && proposal?.response === turn.response ? (
                  <div className="mt-2 space-y-1.5 border-t border-sand-100 pt-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-sand-500">{t("admin:builder.copilot.changes", { count: turn.response.operations.length })}</p>
                    {turn.response.results.map((r) => (
                      <label key={r.id} className={cn("flex items-start gap-2 rounded-md p-1.5", r.error ? "bg-danger-50" : proposal.accepted.has(r.id) ? "bg-success-50/60" : "bg-sand-50")}>
                        <Checkbox checked={proposal.accepted.has(r.id)} disabled={!!r.error || reapply.isPending} onCheckedChange={() => toggle(r.id)} className="mt-0.5" />
                        <span className="min-w-0">
                          <span className="block font-medium">{r.error ? <XCircle className="me-1 inline size-3 text-danger-600" /> : null}{r.summary}</span>
                          {r.rationale ? <span className="block text-[10.5px] text-muted-foreground">{r.rationale}</span> : null}
                          {r.error ? <span className="block text-[10.5px] text-danger-700">{r.error}</span> : null}
                        </span>
                      </label>
                    ))}
                    {errors ? <p className="text-[11px] text-danger-700">{t("admin:builder.copilot.resultErrors", { count: errors })}</p> : null}
                    <div className="flex gap-1.5 pt-1">
                      <Button size="sm" variant="gold" disabled={readOnly || proposal.accepted.size === 0 || reapply.isPending} onClick={accept}><CheckCheck /> {t("admin:builder.copilot.accept", { count: proposal.accepted.size })}</Button>
                      <Button size="sm" variant="ghost" onClick={() => onProposal(null)}><X /> {t("admin:builder.copilot.discard")}</Button>
                    </div>
                  </div>
                ) : turn.response?.operations.length ? <p className="mt-1 text-[10.5px] text-muted-foreground"><Check className="me-1 inline size-3" />{t("admin:builder.copilot.closed")}</p> : null}
              </>
            ) : turn.text}
          </div>
        ))}
        {ask.isPending ? <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" /> {t("admin:builder.copilot.thinking")}</p> : null}
        <div ref={end} />
      </div>
      <div className="space-y-2 border-t border-sand-200 p-3">
        {selectedNodes.length ? <p className="truncate text-[11px] text-muted-foreground">{t("admin:builder.copilot.context")}: {selectedNodes.map((n) => n.label).join(", ")}</p> : null}
        <Textarea rows={3} value={input} onChange={(e) => setInput(e.target.value)} placeholder={t(`admin:builder.copilot.placeholder.${mode}`)} dir="auto"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }} className="text-xs" />
        <div className="flex items-center gap-2">
          <NativeSelect className="h-8 w-40 text-xs" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            <option value="edit" disabled={readOnly}>{t("admin:builder.copilot.modes.edit")}</option>
            <option value="generate" disabled={readOnly}>{t("admin:builder.copilot.modes.generate")}</option>
            <option value="explain">{t("admin:builder.copilot.modes.explain")}</option>
            <option value="explainDiff" disabled={!diffRange}>{t("admin:builder.copilot.modes.explainDiff")}</option>
          </NativeSelect>
          <span className="flex-1" />
          <Button size="sm" variant="gold" loading={ask.isPending} disabled={!input.trim() && mode !== "explain" && mode !== "explainDiff"} onClick={() => send()}><Send /> {t("admin:builder.copilot.send")}</Button>
        </div>
      </div>
    </div>
  );
}
